import { useState } from 'react';
import type { RatioConfig, RatioValue } from '../../lib/fieldValue';
import type { Field } from '../../lib/templates';
import { FootnoteNote } from './FootnoteNote';

interface RatioInputProps {
  field: Pick<Field, 'allowFootnote'>;
  config: RatioConfig;
  value: RatioValue | null;
  onCommit: (value: RatioValue | null) => void;
  disabled?: boolean;
}

/** Two numbers locked to sum to 100: moving the left slot recomputes the right one automatically. */
export function RatioInput({ field, config, value, onCommit, disabled = false }: RatioInputProps) {
  const [left, setLeft] = useState(value?.left ?? 50);
  const [note, setNote] = useState(value?.note ?? '');

  function commit(rawLeft: number) {
    const clamped = Math.max(0, Math.min(100, Math.round(Number.isFinite(rawLeft) ? rawLeft : 0)));
    setLeft(clamped);
    if (disabled) {
      return;
    }
    onCommit({
      type: 'RATIO',
      left: clamped,
      right: 100 - clamped,
      footnote: value?.footnote ?? false,
      note: value?.note ?? null,
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <label className="flex flex-1 flex-col gap-1 text-sm text-text-muted">
          <span className="eyebrow">{config.leftLabel}</span>
          <input
            type="number"
            min={0}
            max={100}
            value={left}
            disabled={disabled}
            onChange={(event) => commit(Number(event.target.value))}
            className="telemetry rounded-md border border-border-strong bg-bg px-3 py-2 text-text focus:border-accent"
          />
        </label>
        <span className="display text-xl text-text-faint" aria-hidden="true">
          /
        </span>
        <label className="flex flex-1 flex-col gap-1 text-sm text-text-muted">
          <span className="eyebrow">{config.rightLabel}</span>
          <input
            type="number"
            value={100 - left}
            disabled
            readOnly
            className="telemetry rounded-md border border-border bg-bg-elevated px-3 py-2 text-text-faint"
          />
        </label>
      </div>
      {field.allowFootnote && (
        <FootnoteNote
          footnote={value?.footnote ?? false}
          note={note}
          disabled={disabled || !value}
          onFootnoteChange={(footnote) => value && onCommit({ ...value, footnote })}
          onNoteChange={setNote}
          onNoteBlur={() => value && onCommit({ ...value, note: note.trim() === '' ? null : note })}
        />
      )}
    </div>
  );
}
